// A ponte entre `inputs.alocacao.alc_c`/`inputs.alloc` (frações por classe,
// como a tool devolve) e as duas pizzas do cartão da simulação no chat — hoje
// × fim da migração (F5, pedido do owner em 08/09/2026).
//
// ★ Espelha `_alocacao_utilizavel`/`_alocacao_comparada` do PDF
// (`aawz-web-backend/src/aawz_fee_fixo/core/relatorio_payload.py`): mesma
// exigência (as 7 classes com fração válida E soma ~1) e a MESMA tolerância
// de soma (`TOLERANCIA_SOMA_PESOS`, já usada pelo chip G1 do card de
// composição — não uma segunda régua divergindo). O PDF e o cartão do chat
// têm que degradar da MESMA forma para o MESMO cenário: "sem alocação no
// PDF" virando "com alocação no chat" faria as duas superfícies contarem
// histórias diferentes sobre o mesmo cliente.
//
// ★ `null` é a degradação CORRETA quando uma das duas alocações não fecha —
// o bloco INTEIRO sai do cartão (mesma regra do `PAGINAS_DA_ALOCACAO` do
// PDF), nunca meia pizza ou uma fatia zerada inventada: um gráfico com dado
// ausente mente para o consultor.
//
// Diferente do PDF, este util NÃO calcula custo por classe (`custo_atual_bps`/
// `custo_projetado_bps`) — só o PESO da alocação, que é tudo que a pizza
// desenha. O custo por classe depende do fator de cashback do CANAL
// (`fator_cashback_do_canal`, cálculo do motor Python) e não está disponível
// no `S` do chat; replicá-lo aqui seria uma segunda implementação do mesmo
// cálculo — o problema que este módulo inteiro existe para evitar (mesma
// razão de `utils/simulacao-do-chat.js`).
import { K, KLABEL, KCOLOR, TOLERANCIA_SOMA_PESOS } from '@modules/fee-fixo/constants/feeLab'

const SOMA_ALVO = 1
const COR_NEUTRA_DE_CLASSE = '#C9CDD3'

/** Fração utilizável, ou `null` — `true`/`false` não é fração (mesma régua do backend, `_fracao`). */
const fracao = (valor) => {
  if (typeof valor === 'boolean' || typeof valor !== 'number' || !Number.isFinite(valor)) {
    return null
  }
  return valor
}

/**
 * A alocação como mapa de frações, ou `null` se ela não fecha.
 *
 * Exige as 7 classes (`K`) com fração válida E soma ~1
 * (`TOLERANCIA_SOMA_PESOS`). Meia alocação produziria uma pizza cujas fatias
 * não somam 100% — ou pior, uma fatia "fantasma" cobrindo o que falta.
 */
const alocacaoUtilizavel = (alocacao) => {
  if (!alocacao || typeof alocacao !== 'object') return null
  const fracoes = {}
  for (const classe of K) {
    const valor = fracao(alocacao[classe])
    if (valor === null) return null
    fracoes[classe] = valor
  }
  const soma = K.reduce((acc, classe) => acc + fracoes[classe], 0)
  if (Math.abs(soma - SOMA_ALVO) > TOLERANCIA_SOMA_PESOS) return null
  return fracoes
}

/**
 * As duas alocações do cenário (hoje × fim da migração), classe a classe —
 * ou `null` quando `inputs.alocacao.alc_c` OU `inputs.alloc` não fecham.
 *
 * @param {Object} inputs O `S` do cartão, como a tool devolveu.
 * @returns {?{linhas: Array<{classe: string, nome: string, cor: string,
 *   atual: number, projetada: number, delta: number}>}} `atual`/`projetada`/
 *   `delta` em FRAÇÃO (0..1) — formatar com `pct()`/`signPctF()` no consumidor,
 *   mesma convenção de `utils/fee-lab-composicao.js`.
 */
export const alocacaoComparadaDoChat = (inputs) => {
  const dados = inputs && typeof inputs === 'object' ? inputs : {}
  const bloco = dados.alocacao && typeof dados.alocacao === 'object' ? dados.alocacao : null
  const atual = alocacaoUtilizavel(bloco && bloco.alc_c)
  const projetada = alocacaoUtilizavel(dados.alloc)
  if (!atual || !projetada) return null

  const linhas = K.map((classe) => ({
    classe,
    nome: KLABEL[classe] || classe,
    cor: KCOLOR[classe] || COR_NEUTRA_DE_CLASSE,
    atual: atual[classe],
    projetada: projetada[classe],
    delta: projetada[classe] - atual[classe],
  }))

  // Ordem de LEITURA — peso ALVO decrescente, mesma régua do PDF
  // (`_alocacao_comparada`): o consultor olha primeiro onde o cliente vai
  // ter mais dinheiro depois da migração. Desempate pela ordem canônica `K`,
  // para duas classes empatadas não trocarem de lugar entre duas renderizações.
  const ordemCanonica = new Map(K.map((classe, indice) => [classe, indice]))
  linhas.sort((a, b) => {
    const porPeso = b.projetada - a.projetada
    if (porPeso !== 0) return porPeso
    return ordemCanonica.get(a.classe) - ordemCanonica.get(b.classe)
  })

  return { linhas }
}
